'use client'

import {
  EntityDetailSkeleton,
  PageHeaderActions,
  PageHeaderLeading,
  CopyPublicLinkButton,
  OrganisationPublicProfileStack,
  PageContainer,
  PageHeader,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  API_ORIGIN,
  useAuth,
  candidateJobDetailPath,
  isOrganisationFollowEnabled,
  isPublicOrganisationsEnabled,
  publicOrganisationUrl,
} from '@yeshuman/sdk'
import type { PublicOrganisation } from '@yeshuman/sdk'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { CandidateOrganisationFollowButton } from './candidate-organisation-follow-button'
import {
  openRoleJobLinkState,
  useCandidateOrganisationBackTarget,
} from '../../hooks/use-navigate-to-candidate-job-detail'

export function CandidateOrganisationPage() {
  const { slug = '' } = useParams<{ slug: string }>()
  const orgBack = useCandidateOrganisationBackTarget()
  const { token } = useAuth()
  const showFollow = isOrganisationFollowEnabled() && Boolean(token)

  const [organisation, setOrganisation] = useState<PublicOrganisation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const trimmed = slug.trim()
      if (!trimmed || !isPublicOrganisationsEnabled()) {
        if (!cancelled) {
          setOrganisation(null)
          setError('Organisation not found')
          setLoading(false)
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/public/organisations/${encodeURIComponent(trimmed)}?include_jobs=true`,
        )
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Organisation not found')
        if (!cancelled) setOrganisation(data as PublicOrganisation)
      } catch (err) {
        if (!cancelled) {
          setOrganisation(null)
          setError(err instanceof Error ? err.message : 'Organisation not found')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  const header = (
    <PageHeader
      leading={
        <PageHeaderLeading
          back={{
            label: orgBack.label,
            to: orgBack.to,
            state: orgBack.state,
          }}
        />
      }
    />
  )

  if (loading) {
    return (
      <PageContainer>
        {header}
        <EntityDetailSkeleton
          variant="profile"
          withPageContainer={false}
          includeJobsSection
          ariaLabel="Loading organisation"
        />
      </PageContainer>
    )
  }

  if (error || !organisation) {
    return (
      <PageContainer>
        {header}
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Organisation unavailable</CardTitle>
            <CardDescription alwaysShow>
              {error || 'This organisation could not be loaded.'}
            </CardDescription>
          </CardHeader>
        </Card>
      </PageContainer>
    )
  }

  const shareUrl = publicOrganisationUrl(organisation.slug)

  return (
    <PageContainer>
      {header}
      <OrganisationPublicProfileStack
        organisation={organisation}
        shareUrl={shareUrl}
        actions={
          <PageHeaderActions
            tertiary={<CopyPublicLinkButton url={shareUrl} iconOnly />}
            primary={
              showFollow ? <CandidateOrganisationFollowButton slug={organisation.slug} /> : null
            }
          />
        }
        rolesClassName="scroll-mt-4"
        jobHref={(job) => candidateJobDetailPath({ public_slug: job.slug, id: job.id })}
        jobLinkState={(job) =>
          openRoleJobLinkState(job.slug, organisation, orgBack.fromJob)
        }
      />
    </PageContainer>
  )
}
